import { useState } from 'react';

import { Button, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import { StoreOptionsProps } from '../../autocompletes/StoreAutoComplete';

import { DownloadModeloArquivoBtn } from 'components/buttons';
import { ContentModal } from 'components/modal';
import { SubTitle } from 'components/text';
import { UploadButton } from 'components/upload';
import { useUploadFileMutation } from 'mutations/useUploadFileMutation';

export interface StoreImportModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    onImportSuccess: (lojas: StoreOptionsProps[]) => void;
}

export function StoreImportModal({ open, setOpen, onImportSuccess }: StoreImportModalProps) {
    const [fileLojas, setFileLojas] = useState<File | null>(null);
    const { enqueueSnackbar } = useSnackbar();

    const { mutate, isPending } = useUploadFileMutation({
        onSuccess: (response: any) => {
            const { lojas, lojasNaoEncontradas } = response.data;

            const filteredLojas = lojas.map((loja: any) => ({
                idLoja: loja.idLoja,
                dsRazaoSocial: loja.dsRazaoSocial,
                dsNomeFantasia: loja.dsNomeFantasia,
                cdLoja: loja.cdLoja,
            }));

            enqueueSnackbar('Importação de lojas realizada com sucesso', { variant: 'success' });

            if (filteredLojas.length > 0) {
                onImportSuccess(filteredLojas);
            }

            if (lojasNaoEncontradas.length > 0) {
                enqueueSnackbar(`Lojas não encontradas: ${lojasNaoEncontradas.join(', ')}`, { variant: 'warning' });
            }

            setOpen(false);

            setFileLojas(null);
        },
        onError: () => enqueueSnackbar('Falha ao importar as lojas', { variant: 'error' }),
    });

    /**
     * Importa as lojas do arquivo CSV.
     */
    const onClickImportarLojas = () => {
        const formData = new FormData();

        if (fileLojas) {
            formData.append('file', fileLojas);
        }

        mutate({
            url: '/gestao/loja/arquivo',
            formData,
        });
    };

    return (
        <ContentModal
            open={open}
            setOpen={setOpen}
            closeButton={!isPending}
            disableEscapeKeyDown={isPending}
            disableBackdropClick={isPending}
            sx={{
                width: 400,
                borderRadius: 1,
            }}
        >
            <Stack>
                <SubTitle label="Importar Lojas" />

                <UploadButton label="Selecionar Arquivo" accept={['.csv']} onChange={(file) => setFileLojas(file)} />

                {fileLojas && (
                    <Typography variant="caption" component="p" sx={{ mt: 1 }}>
                        {fileLojas.name}
                    </Typography>
                )}

                <Typography variant="caption" component="p" sx={{ mt: 1 }}>
                    *São aceitos somente arquivos em formato .csv
                </Typography>

                <Stack direction="row">
                    <DownloadModeloArquivoBtn sx={{ flex: 1 }} dsChave="URL_ARQUIVO_MODELO_LOJA" />
                </Stack>

                <Stack spacing={2} direction="row" sx={{ justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="outlined"
                        disabled={isPending}
                        onClick={() => {
                            setOpen(false);
                            setFileLojas(null);
                        }}
                    >
                        Cancelar
                    </Button>

                    <Button variant="contained" onClick={onClickImportarLojas} disabled={!fileLojas} loading={isPending}>
                        Importar Lojas
                    </Button>
                </Stack>
            </Stack>
        </ContentModal>
    );
}
